import { proxy } from 'valtio';

export enum Role {
  HOST = 'host',
  GUEST = 'guest',
  ADMIN = 'admin',
}

type NonAdminRole = Role.GUEST | Role.HOST;

const LAST_NON_ADMIN_ROLE_KEY = 'lastNonAdminRole';

interface RoleState {
  role: Role;
  lastNonAdminRole: NonAdminRole;
}

export const roleStore = proxy<RoleState>({
  role: Role.GUEST,
  lastNonAdminRole: Role.GUEST,
});

const isNonAdminRole = (value: string | null): value is NonAdminRole =>
  value === Role.GUEST || value === Role.HOST;

const persistLastNonAdminRole = (role: NonAdminRole) => {
  roleStore.lastNonAdminRole = role;
  sessionStorage.setItem(LAST_NON_ADMIN_ROLE_KEY, role);
};

const restoreLastNonAdminRole = () => {
  const stored = sessionStorage.getItem(LAST_NON_ADMIN_ROLE_KEY);
  if (isNonAdminRole(stored)) {
    roleStore.lastNonAdminRole = stored;
  }
};

export const roleActions = {
  setRole: (role: Role) => {
    roleStore.role = role;
    localStorage.setItem('role', role);
  },

  enterAdminMode: () => {
    if (roleStore.role === Role.ADMIN) {
      return;
    }
    if (roleStore.role === Role.GUEST || roleStore.role === Role.HOST) {
      persistLastNonAdminRole(roleStore.role);
    }
    roleStore.role = Role.ADMIN;
    localStorage.setItem('role', Role.ADMIN);
  },

  exitAdminMode: (options?: { isHost?: boolean }) => {
    let restored: NonAdminRole = roleStore.lastNonAdminRole;
    if (restored === Role.HOST && !options?.isHost) {
      restored = Role.GUEST;
    }
    roleStore.role = restored;
    localStorage.setItem('role', restored);
    sessionStorage.removeItem(LAST_NON_ADMIN_ROLE_KEY);
  },

  initRole: (
    isAuthenticated: boolean,
    user?: { isAdmin?: boolean | null; isHost?: boolean | null } | null,
  ) => {
    if (!isAuthenticated) {
      roleStore.role = Role.GUEST;
      roleStore.lastNonAdminRole = Role.GUEST;
      return;
    }

    restoreLastNonAdminRole();

    const storedRole = localStorage.getItem('role');
    if (!storedRole) {
      return;
    }

    if (storedRole === Role.ADMIN) {
      if (!user?.isAdmin) {
        roleActions.setRole(Role.GUEST);
        sessionStorage.removeItem(LAST_NON_ADMIN_ROLE_KEY);
        return;
      }
      roleStore.role = Role.ADMIN;
      return;
    }

    if (storedRole === Role.HOST) {
      if (!user?.isHost) {
        roleActions.setRole(Role.GUEST);
        return;
      }
      roleStore.role = Role.HOST;
      return;
    }

    if (storedRole === Role.GUEST) {
      roleStore.role = Role.GUEST;
    }
  },

  resetRole: () => {
    roleStore.role = Role.GUEST;
    roleStore.lastNonAdminRole = Role.GUEST;
    localStorage.removeItem('role');
    sessionStorage.removeItem(LAST_NON_ADMIN_ROLE_KEY);
  },
};
